/**
 * Pricing System Settings — labels and layout for admin UI (W15-E02).
 * @see docs/bubakii-v2/wave-1.5-provider-marketplace/pricing-engine-first-slice.md
 */

/**
 * First-class Pricing Settings keys shown in admin UI.
 * Engine may still load demoted keys (see DEMOTED_PRICING_SETTING_KEYS) for back-compat.
 */
/** @type {readonly string[]} */
export const PRICING_SETTING_KEYS = Object.freeze([
  "pricing.automation.enabled",
  "pricing.automation.truckLedLocal.enabled",
  "pricing.policy.version",
  "pricing.local.maxDistanceKm",
  "pricing.local.includedKm",
  "pricing.local.defaultService",
  "pricing.local.baseAmount",
  "pricing.intraProvince.ratePerKm",
  "pricing.inventory.nonStackableFactor",
  "pricing.history.reviewVariancePercent",
  "pricing.quotes.expiryDays",
  "pricing.access.longCarry.thresholdMetres",
  "pricing.resources.additionalTruckAmount",
  "pricing.resources.additionalTripAmount",
  "pricing.resources.specialUnitAmount",
  "pricing.access.stairs.fixedAmount",
  "pricing.access.restrictedLift.fixedAmount",
  "pricing.access.longCarry.fixedAmount",
  "pricing.access.difficultVehicleAccess.fixedAmount",
  "pricing.access.noLoadingZone.fixedAmount",
  "pricing.adjustments.approvalMode",
  "pricing.rounding.increment",
  "pricing.tax.vatApplicable",
  "pricing.vehicles.bands",
  "pricing.economics.estimatedProviderCostPercent",
  "pricing.economics.split.enabled",
  "pricing.economics.customerMarkup.percent",
  "pricing.economics.contribution.percent",
  "pricing.economics.contribution.minAmountPerJob",
  "pricing.economics.shared.minCustomerTotal",
  "pricing.economics.providerCut.enabled",
  "pricing.economics.providerCut.until",
  "pricing.corridors.enabled",
  "pricing.corridors.catalogue",
  "pricing.corridors.locationRegionMap",
  "pricing.exclusive.enabled",
  "pricing.exclusive.catalogue",
  "pricing.season.enabled",
  "pricing.season.defaultCode",
  "pricing.season.calendar",
  "pricing.intraProvince.ratePerKmBySeason",
]);

/**
 * Demoted from primary Pricing Settings UI — loader/engine may still read these.
 * Rate-card monetary groups are edited under Pricing → Rate Cards, not here.
 * Legacy longDistance / packingFactor kept for back-compat only.
 */
export const DEMOTED_PRICING_SETTING_KEYS = Object.freeze([
  // Legacy (not Rate Cards, but not first-class UI)
  "pricing.longDistance.ratePerM3Km",
  "pricing.inventory.packingFactor",
  // Ported to Rate Cards — Shared Load
  "pricing.corridors.enabled",
  "pricing.corridors.catalogue",
  "pricing.corridors.locationRegionMap",
  // Ported to Rate Cards — Exclusive LD
  "pricing.exclusive.enabled",
  "pricing.exclusive.catalogue",
  // Ported to Rate Cards — Local / Intra
  "pricing.automation.truckLedLocal.enabled",
  "pricing.local.maxDistanceKm",
  "pricing.local.includedKm",
  "pricing.local.baseAmount",
  "pricing.intraProvince.ratePerKm",
  "pricing.intraProvince.ratePerKmBySeason",
  "pricing.vehicles.bands",
  // Ported to Rate Cards — Access & Handling
  "pricing.access.longCarry.thresholdMetres",
  "pricing.access.stairs.fixedAmount",
  "pricing.access.restrictedLift.fixedAmount",
  "pricing.access.longCarry.fixedAmount",
  "pricing.access.difficultVehicleAccess.fixedAmount",
  "pricing.access.noLoadingZone.fixedAmount",
]);

/** Retired keys — hide even if leftover DB rows exist; seed deletes them. */
export const RETIRED_PRICING_SETTING_KEYS = Object.freeze([
  "pricing.economics.contributionFloor.enabled",
  "pricing.economics.operatingRecovery.percentOfProviderCost",
  "pricing.economics.operatingRecovery.minAmountPerJob",
  "pricing.economics.contributionMargin.targetPercent",
  "pricing.corridors.regions",
]);

/** Required before auto-pricing can emit customer totals. */
export const CORE_MONETARY_KEYS = Object.freeze(["pricing.local.baseAmount"]);

/**
 * @typedef {object} PricingFieldMeta
 * @property {string} label
 * @property {string} section
 * @property {"boolean"|"string"|"number"|"array"} type
 * @property {string} [description]
 * @property {object} [metadata]
 * @property {boolean} [monetary]
 */

/** @type {Record<string, PricingFieldMeta>} */
export const PRICING_FIELD_META = Object.freeze({
  "pricing.automation.enabled": {
    label: "Enable automated pricing",
    section: "automation",
    type: "boolean",
    description:
      "ON = system drafts a customer total after the lead is ready. OFF = staff price it. A person still reviews before send.",
  },
  "pricing.automation.truckLedLocal.enabled": {
    label: "Truck-led same-province pricing",
    section: "automation",
    type: "boolean",
    description:
      "ON = price by truck size (e.g. 8 m³ → 1-ton base R2 500). OFF = everyone starts at the flat Local base amount.",
  },
  "pricing.policy.version": {
    label: "Policy version",
    section: "automation",
    type: "string",
    description:
      "A label saved on each quote (e.g. w15-e02-v1) so you can see which rules created it. Not a price.",
  },
  "pricing.local.maxDistanceKm": {
    label: "Local boundary (km, legacy)",
    section: "local",
    type: "number",
    metadata: { min: 1, max: 500, unit: "km" },
    description:
      "Backup only if Included km is empty. Example: 50. Prefer Included km.",
  },
  "pricing.local.includedKm": {
    label: "Included km (same province)",
    section: "local",
    type: "number",
    metadata: { min: 1, max: 500, unit: "km" },
    description:
      "Same-province km covered by the truck/flat base. Example: 50 → a 35 km trip has no extra km; an 80 km trip adds 30 km × the intra-province rate.",
  },
  "pricing.local.defaultService": {
    label: "Default local service",
    section: "policy",
    type: "string",
    description:
      "Usually exclusive_load (dedicated truck). Change only if local jobs should default to shared. Monetary rates are under Pricing → Rate Cards.",
  },
  "pricing.local.baseAmount": {
    label: "Local base amount (ZAR)",
    section: "local",
    type: "number",
    monetary: true,
    metadata: { min: 0, step: 50, unit: "ZAR" },
    description:
      "Starting local price when truck-led is OFF. Example: 2500 → a standard local exclusive move starts at R2 500 before access extras.",
  },
  "pricing.intraProvince.ratePerKm": {
    label: "Intra-province rate (ZAR/km)",
    section: "intraProvince",
    type: "number",
    monetary: true,
    metadata: { min: 0, step: 1, unit: "ZAR/km" },
    description:
      "Extra km past Included km, same province, on top of the truck base. Example: 15 → 30 extra km = R450.",
  },
  // Demoted from primary UI — meta kept for residual lookups / docs.
  "pricing.longDistance.ratePerM3Km": {
    label: "Shared long-distance rate (ZAR per m³·km)",
    section: "longDistance",
    type: "number",
    monetary: true,
    metadata: { min: 0, step: 1, unit: "ZAR" },
    description:
      "Legacy shared formula when corridors are OFF: m³ × extra km × this rate. Not a first-class Pricing section — prefer corridors.",
  },
  "pricing.inventory.packingFactor": {
    label: "Packing factor (deprecated)",
    section: "inventory",
    type: "number",
    monetary: true,
    metadata: { min: 0, step: 0.05 },
    description:
      "Ignored by the engine. Demoted from Pricing UI — sell packing as catalogue products.",
  },
  "pricing.inventory.nonStackableFactor": {
    label: "Non-stackable factor",
    section: "inventory",
    type: "number",
    monetary: true,
    metadata: { min: 0, step: 0.05 },
    description:
      "Extra billable volume when items cannot be stacked. Example: 1.15 on 10 m³ → we price 11.5 m³.",
  },
  "pricing.history.reviewVariancePercent": {
    label: "Historical variance threshold (%)",
    section: "policy",
    type: "number",
    metadata: { min: 0, max: 100, unit: "percent" },
    description:
      "If the new price is this % away from similar past jobs, a person must check. Example: 20 → R10 000 vs past R7 000 is flagged.",
  },
  "pricing.quotes.expiryDays": {
    label: "Provisional quote expiry (days)",
    section: "policy",
    type: "number",
    metadata: { min: 1, max: 90, unit: "days" },
    description:
      "How long a draft auto-quote stays valid. Example: 7 → created Monday, refresh by the next Monday.",
  },
  "pricing.access.longCarry.thresholdMetres": {
    label: "Long-carry threshold (m)",
    section: "access",
    type: "number",
    metadata: { min: 1, max: 200, unit: "metres" },
    description:
      "Walk from truck to door longer than this → add the Long carry fee. Example: 20 m; a 35 m carry is charged.",
  },
  "pricing.resources.additionalTruckAmount": {
    label: "Additional truck charge (ZAR)",
    section: "resources",
    type: "number",
    monetary: true,
    metadata: { min: 0, step: 50, unit: "ZAR" },
    description:
      "Per extra truck after admin confirms. Example: R1 500 × 2 extra trucks = + R3 000.",
  },
  "pricing.resources.additionalTripAmount": {
    label: "Additional trip charge (ZAR)",
    section: "resources",
    type: "number",
    monetary: true,
    metadata: { min: 0, step: 50, unit: "ZAR" },
    description:
      "Per extra trip after admin confirms. Example: R800 for a second run.",
  },
  "pricing.resources.specialUnitAmount": {
    label: "Special resource unit charge (ZAR)",
    section: "resources",
    type: "number",
    monetary: true,
    metadata: { min: 0, step: 50, unit: "ZAR" },
    description:
      "Per specialist unit after admin confirms. Example: R500 × 2 hoists = + R1 000.",
  },
  "pricing.access.stairs.fixedAmount": {
    label: "Stairs access (ZAR)",
    section: "access",
    type: "number",
    monetary: true,
    metadata: { min: 0, step: 50, unit: "ZAR" },
    description:
      "When the crew must use stairs at a stop. Example: R250 at a 2nd-floor walk-up delivery.",
  },
  "pricing.access.restrictedLift.fixedAmount": {
    label: "Restricted lift (ZAR)",
    section: "access",
    type: "number",
    monetary: true,
    metadata: { min: 0, step: 50, unit: "ZAR" },
    description:
      "Lift exists but cannot be used normally (too small, booked, broken). Example: R200 if a fridge will not fit.",
  },
  "pricing.access.longCarry.fixedAmount": {
    label: "Long carry (ZAR)",
    section: "access",
    type: "number",
    monetary: true,
    metadata: { min: 0, step: 50, unit: "ZAR" },
    description:
      "Amount added when the walk is longer than the threshold. Example: R300 for a 40 m carry (threshold 20 m).",
  },
  "pricing.access.difficultVehicleAccess.fixedAmount": {
    label: "Difficult vehicle access (ZAR)",
    section: "access",
    type: "number",
    monetary: true,
    metadata: { min: 0, step: 50, unit: "ZAR" },
    description:
      "Truck cannot reach a normal loading point. Example: R350 when it must park in the street.",
  },
  "pricing.access.noLoadingZone.fixedAmount": {
    label: "No loading zone (ZAR)",
    section: "access",
    type: "number",
    monetary: true,
    metadata: { min: 0, step: 50, unit: "ZAR" },
    description:
      "No legal parking/loading near the door. Example: R200 at a CBD tower with no loading bay.",
  },
  "pricing.adjustments.approvalMode": {
    label: "Ad-hoc adjustment approval",
    section: "policy",
    type: "string",
    description:
      "Leave as always — every one-off discount or surcharge needs admin approval before send.",
  },
  "pricing.rounding.increment": {
    label: "Rounding increment (ZAR)",
    section: "policy",
    type: "number",
    metadata: { min: 1, max: 100, unit: "ZAR" },
    description:
      "Draft totals round to the nearest this many rand. Example: 10 → R2 547 becomes R2 550.",
  },
  "pricing.tax.vatApplicable": {
    label: "Apply VAT",
    section: "policy",
    type: "boolean",
    description:
      "OFF = draft total has no 15% VAT (current rule). Turn ON only when finance says VAT must show.",
  },
  "pricing.vehicles.bands": {
    label: "Vehicle capacity + truck bases",
    section: "vehicles",
    type: "array",
    description:
      "Truck menu: size, how much it holds, customer label (Small Move), included crew, and exclusive baseAmount. Example: 8 m³ fits a 1-ton band with maxBillableM3 10 and base R2 500.",
  },
  "pricing.economics.estimatedProviderCostPercent": {
    label: "Estimated provider cost (% of quote)",
    section: "economics",
    type: "number",
    monetary: false,
    metadata: { min: 0, max: 100, unit: "percent" },
    description:
      "Fallback only when commercial split did not set a driver offer (e.g. split OFF or no automated total). Staff-only. 0 = do not guess.",
  },
  "pricing.economics.split.enabled": {
    label: "Enable commercial split (cost + markup / cost − contribution)",
    section: "economics",
    type: "boolean",
    description:
      "ON = customer pays cost + markup; drivers see cost − contribution. Applies to local, intra, shared, and exclusive LD when an automated total exists. Shared also uses the shared-load customer minimum. Skipped when exclusive is still policy-pending (no total).",
  },
  "pricing.economics.customerMarkup.percent": {
    label: "Customer markup % (on job cost)",
    section: "economics",
    type: "number",
    monetary: false,
    metadata: { min: 0, max: 100, unit: "percent" },
    description:
      "Added to job cost for the customer (local, intra, shared, exclusive LD). 20 on R10 000 → customer R12 000. Requires commercial split ON.",
  },
  "pricing.economics.contribution.percent": {
    label: "Contribution % (cut from driver offer)",
    section: "economics",
    type: "number",
    monetary: false,
    metadata: { min: 0, max: 100, unit: "percent" },
    description:
      "Taken from the advertised driver amount while provider cut is on (all split products). 10 on R10 000 → advertise R9 000.",
  },
  "pricing.economics.contribution.minAmountPerJob": {
    label: "Contribution minimum (per job)",
    section: "economics",
    type: "number",
    monetary: true,
    metadata: { min: 0, unit: "ZAR" },
    description:
      "Smallest Rand cut from the driver offer if the % would be tiny. 0 = percent only. All split products.",
  },
  "pricing.economics.shared.minCustomerTotal": {
    label: "Shared-load customer minimum",
    section: "economics",
    type: "number",
    monetary: true,
    metadata: { min: 0, unit: "ZAR" },
    description:
      "Shared-load only (interprovince shared). Never quote that customer below this after markup. Does not apply to local, intra, or exclusive LD.",
  },
  "pricing.economics.providerCut.enabled": {
    label: "Cut contribution from driver offer",
    section: "economics",
    type: "boolean",
    description:
      "ON = advertise cost − contribution. OFF = advertise full cost. Customer still pays cost + markup. All split products.",
  },
  "pricing.economics.providerCut.until": {
    label: "Provider cut end date (inclusive)",
    section: "economics",
    type: "string",
    description:
      "Last day the contribution haircut applies (YYYY-MM-DD). Empty = stay on until you turn the switch off.",
  },
  "pricing.corridors.enabled": {
    label: "Enable corridor pricing (live)",
    section: "corridors",
    type: "boolean",
    description:
      "ON = shared other-province jobs use the Bubakii corridor catalogue (higher of minimum or m³ × rate). Same-province never uses this.",
  },
  "pricing.corridors.catalogue": {
    label: "Corridor pricing catalogue",
    section: "corridors",
    type: "array",
    description:
      "Bubakii commercial region-pair price list for shared loads. originRegion/destinationRegion are pricing region codes (default = provinces). Example: GP–WC min R1 500 and R390/m³ → 3 m³ = R1 500; 20 m³ = R7 800. Optional reserved fields direction/season/mileageAdjustment are ignored until activated.",
  },
  "pricing.corridors.locationRegionMap": {
    label: "Location → pricing region map",
    section: "corridors",
    type: "array",
    description:
      "Optional map: { matchType: city|suburb|town|postalCode|hub, matchValue, regionCode, active? }. Empty = fall back to province for corridor matching. Product routing always uses provinces.",
  },
  "pricing.exclusive.enabled": {
    label: "Enable exclusive LD pricing (live)",
    section: "exclusive",
    type: "boolean",
    description:
      "ON = interprovince exclusive loads use the exclusive catalogue (truck × region flat rate). OFF = exclusive_policy_pending (manual customer price). Private bidding / no public board (ADR-005) stays the same.",
  },
  "pricing.exclusive.catalogue": {
    label: "Exclusive LD catalogue",
    section: "exclusive",
    type: "array",
    description:
      "Rows: { id, originRegion, destinationRegion, truckSize, flatAmount, minimumAmount?, enabled }. truckSize must match a vehicle band. Prefer Rate Cards UI. Do not import competitor dedicated-load sheets as defaults.",
  },
  "pricing.season.enabled": {
    label: "Enable pricing seasons",
    section: "season",
    type: "boolean",
    description:
      "ON = move date selects peak / off_peak / mid_month / month_end / standard from the calendar; rate cards can have seasonal rows. OFF = always use default season code.",
  },
  "pricing.season.defaultCode": {
    label: "Default season code",
    section: "season",
    type: "string",
    description: "Used when no calendar window matches (usually standard).",
  },
  "pricing.season.calendar": {
    label: "Season calendar",
    section: "season",
    type: "array",
    description:
      "Windows: { id, code, start, end, kind?, startDay?, endDay?, regions?, priority?, active? }. MM-DD yearly or day_of_month (mid-month 1–20 / month-end 21–31). Codes: peak, off_peak, mid_month, month_end, standard.",
  },
  "pricing.intraProvince.ratePerKmBySeason": {
    label: "Intra rate/km by season",
    section: "intraProvince",
    type: "object",
    description:
      "Optional map { peak, off_peak, mid_month, month_end, standard } → R/km. Empty = use the single rate/km for all seasons. Aliases: mid_month↔off_peak, month_end↔peak.",
  },
});

/**
 * First-class Bubakii rate-card groups (engines already use these).
 * Not competitor categories (no Dedicated LD / Peak / Depo-to-Depo / Garden Route sections).
 * Legacy m³·km long-distance is demoted — see DEMOTED_PRICING_SETTING_KEYS.
 */
/** @type {{ key: string, title: string, hint?: string }[]} */
export const PRICING_SECTIONS = Object.freeze([
  {
    key: "automation",
    title: "Automation & policy version",
    hint: "Keep automation OFF until Rate Cards are filled. Monetary rates and catalogues are edited under Pricing → Rate Cards.",
  },
  {
    key: "season",
    title: "Pricing seasons",
    hint: "Calendar that decides peak vs off-peak from the move date. Rate Cards hold seasonal amounts; this only picks the season code.",
  },
  {
    key: "resources",
    title: "Additional resources",
    hint: "Extra trucks, trips, or specialist kit — only after a person confirms they are needed.",
  },
  {
    key: "inventory",
    title: "Inventory factors",
    hint: "Non-stackable can add extra cube, e.g. 1.15 on 10 m³ → 11.5 m³. Packing is sold as catalogue products (not a transport multiplier).",
  },
  {
    key: "policy",
    title: "Review, expiry & rounding",
    hint: "When a person must check a quote, how long a draft lasts, rounding (e.g. nearest R10), VAT, default service, and approval of one-off changes.",
  },
  {
    key: "economics",
    title: "Internal economics",
    hint: "Staff-only. Commercial split (when ON): markup + contribution for local, intra, shared, and exclusive LD with an automated total. Shared-load customer minimum is shared-only. Estimated provider % is a fallback when split did not set a driver offer.",
  },
]);

/**
 * @param {string} key
 * @returns {PricingFieldMeta}
 */
export function getPricingFieldMeta(key) {
  return (
    PRICING_FIELD_META[key] || {
      label: key.split(".").pop(),
      section: "policy",
      type: "string",
    }
  );
}
